import React from 'react';

import * as ManifestUI from '@project44/manifest-ds';

import { ReactComponent } from '../../../../../shared/abstract/infrastructrue/ReactComponent';
import { ComponentPropsValueObject } from '../../../../../shared/core/domain/ComponentPropsValueObject';
import { MappedTypedDictionaryFromUnionType } from '../../../../../shared/core/infrastructrue/types';
import { TTypography, Typography } from './Typography.view';

export class TypographyComponent extends ReactComponent {
  public handleHighlightableContentWhenPresent(payload: {
    typographyProps: TTypography['typographyProps'];
    highlightableConfig: TTypography['highlightableConfig'];
  }): React.ReactElement {
    const { highlightableConfig, typographyProps } = payload;

    const shouldByPassHighlight = new Array<boolean>(
      highlightableConfig?.searchQuery.length === 0,
    ).includes(true);

    return highlightableConfig && !shouldByPassHighlight
      ? this.createHighlightableTypographyComponent(
          typographyProps,
          highlightableConfig,
        )
      : this.createDefaultTypographyComponent(typographyProps);
  }

  public static create(
    payload: ComponentPropsValueObject<Omit<TTypography, 'config'>>,
  ): React.ReactElement<TTypography> {
    const { withLoadingStatusOptions } = payload.props;

    const typographyProps: TTypography = {
      ...payload.props,
      typographyProps: {
        ...payload.props.typographyProps,
        css: {
          ...payload.props.typographyProps.css,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
      config: new TypographyComponent(),
    };

    return this.composeWithLoadingStatus(Typography, {
      withLoadingStatusOptions: {
        ...withLoadingStatusOptions,
        css: this.modelSkeletonCSSBasedOnVariant(
          payload.props.typographyProps.variant,
        ),
      },
    })(typographyProps);
  }

  public static modelSkeletonCSSBasedOnVariant = (
    typographyVariant: ManifestUI.TypographyProps['variant'],
  ): ManifestUI.CSS | undefined => {
    const bodySettings = {
      h: '14px',
      marginTop: '2px',
      w: '100%',
    };

    const captionSettings = {
      h: '12px',
      marginTop: '6px',
      w: '100%',
    };

    const mappedTypedDictionary: MappedTypedDictionaryFromUnionType<
      NonNullable<ManifestUI.TypographyProps['variant']>,
      ManifestUI.CSS
    > = {
      body: bodySettings,
      bodyBold: bodySettings,
      caption: captionSettings,
      captionBold: captionSettings,
      display: { h: '16px', w: '100%' },
      heading: { h: '16px', w: '100%' },
      subtext: { h: '16px', w: '100%' },
      subtextBold: { h: '16px', w: '100%' },
      subtitle: { h: '16px', w: '100%' },
      title: { h: '16px', w: '100%' },
    };

    return typeof typographyVariant === 'string'
      ? mappedTypedDictionary[typographyVariant]
      : undefined;
  };

  public static compose(
    payload: Omit<TTypography, 'config'>,
  ): React.ReactElement<TTypography> {
    return this.create(ComponentPropsValueObject.create(payload));
  }

  private createDefaultTypographyComponent(
    typographyProps: TTypography['typographyProps'],
  ): React.ReactElement {
    return React.createElement(
      ManifestUI.Typography,
      typographyProps,
      typographyProps.content,
    );
  }

  private createHighlightableTypographyComponent(
    typographyProps: TTypography['typographyProps'],
    highlightableConfig: NonNullable<TTypography['highlightableConfig']>,
  ): React.ReactElement {
    const {
      colorPalettePink50,
      colorPalettePink800,
      radiusSmall,
      fontWeightSemibold,
    } = ManifestUI.designTokens;

    const backgroundColor: string = `background-color:${colorPalettePink50}`;
    const borderRadius: string = `border-radius:${radiusSmall}`;
    const fontWeight: string = `font-weight:${fontWeightSemibold}`;
    const padding: string = `padding:0 1px`;
    const textColor: string = `color:${colorPalettePink800}`;

    const inlineStyles: string = [
      backgroundColor,
      borderRadius,
      fontWeight,
      padding,
      textColor,
    ].join(';');

    return React.createElement(ManifestUI.Typography, {
      ...typographyProps,
      dangerouslySetInnerHTML: {
        __html: typographyProps.content.replace(
          new RegExp(highlightableConfig.searchQuery, 'gi'),
          // The mark element should not be given an accessible name;
          // both aria-label and aria-labelledby attributes are prohibited on mark.
          `<mark data-testid='highlighted-text' style="${inlineStyles}">$&</mark>`,
        ),
      },
    });
  }
}
